import montserrat500 from '@fontsource/montserrat/files/montserrat-cyrillic-500-normal.woff2?url'
import montserrat600 from '@fontsource/montserrat/files/montserrat-cyrillic-600-normal.woff2?url'
import { config } from '@fortawesome/fontawesome-svg-core'
import fontStyle from '@fortawesome/fontawesome-svg-core/styles.css?url'
import { captureException } from '@sentry/react-router'
import { isRouteErrorResponse, Link, Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation } from 'react-router'
import LiveAnnouncement from 'ui-lib/LiveAnnouncement'
import willReloadStaleChunk from 'ui-lib/willReloadStaleChunk'

import CenterContainerComponent from '~/components/CenterContainer'
import LoadingSpinnerComponent from '~/components/LoadingSpinner'
import UTMQueryClientContainer from '~/containers/UTMQueryClient'
import globalCss from '~/globals.css?url'
import type { Route } from '~types/+types/root'

config.autoAddCss = false

export const links: Route.LinksFunction = () => [
	{ as: 'style', href: globalCss, rel: 'preload' },
	{ href: globalCss, rel: 'stylesheet' },
	{ as: 'style', href: fontStyle, rel: 'preload' },
	{ href: fontStyle, rel: 'stylesheet' },
	{ as: 'font', crossOrigin: 'anonymous', href: montserrat500, rel: 'preload', type: 'font/woff2' },
	{ as: 'font', crossOrigin: 'anonymous', href: montserrat600, rel: 'preload', type: 'font/woff2' },
]

const App = () => (
	<UTMQueryClientContainer>
		<Outlet />
		<LiveAnnouncement />
	</UTMQueryClientContainer>
)

/* v8 ignore next 5 */
export const HydrateFallback = () => (
	<main className="h-screen overflow-hidden">
		<CenterContainerComponent data-testid="loading-component">
			<LoadingSpinnerComponent />
		</CenterContainerComponent>
	</main>
)

const trailURL = (url: string): string => url.replace(/\/+$/, '')

/* v8 ignore start */
export const ErrorBoundary = ({ error }: Route.ErrorBoundaryProps) => {
	let message = 'Щось пішло не так...'
	let details = 'Сталася неочікувана помилка.'

	if (isRouteErrorResponse(error)) {
		if (error.status === 404) {
			message = 'Сторінку не знайдено (404)'
			details = 'Сторінка, яку ви шукаєте, не існує.'
		} else {
			message = `Помилка ${String(error.status)}`
			details = typeof error.data === 'string' ? error.data : error.statusText || details
		}
	} else if (error instanceof Error) {
		captureException(error)

		// Старий чанк після деплою: перезавантажуємось замість екрана помилки (подія в Sentry
		// вже пішла — сигнал про поламаний деплой лишається).
		if (willReloadStaleChunk(error)) return null

		details = 'Деталі помилки вже відправлено розробникам.'

		if (import.meta.env.DEV) {
			console.error('Development error:', error)
			console.error(error.stack)
			details = error.message
		}
	} else {
		if (import.meta.env.DEV) {
			console.error('Unknown error type:', error)
		}
	}

	return (
		<main className="h-screen overflow-hidden">
			<CenterContainerComponent className="flex-col gap-3" data-testid="root-error">
				<h1 className="font-semibold">{message}</h1>
				<p className="text-center font-medium" data-testid="root-error-message">
					{details}
				</p>
				<Link className="text-app-red underline" to="/">
					Головна
				</Link>
			</CenterContainerComponent>
		</main>
	)
}
/* v8 ignore stop */

export const Layout = ({ children }: { children: React.ReactNode }) => {
	const location = useLocation()
	return (
		<html lang="uk" translate="no">
			<head>
				<meta charSet="utf-8" />
				<meta content="width=device-width, initial-scale=1.0" name="viewport" />
				<meta content="telephone=no, address=no, email=no" name="format-detection" />
				<meta content="Особистий кабінет користувача Langate" name="description" />
				<link href="/favicon.ico" rel="icon" type="image/x-icon" />
				<link href="/favicon-16x16.png" rel="icon" sizes="16x16" type="image/png" />
				<link href="/favicon-32x32.png" rel="icon" sizes="32x32" type="image/png" />
				<link href="/favicon-48x48.png" rel="icon" sizes="48x48" type="image/png" />
				<link href={`https://test.langate.ua${trailURL(location.pathname)}`} rel="canonical" />
				<link href="/manifest.webmanifest" rel="manifest" />
				<meta content="yes" name="mobile-web-app-capable" />
				<meta content="#565654" name="theme-color" />
				<meta content="Langate" name="application-name" />
				<link href="/apple-touch-icon-57x57.png" rel="apple-touch-icon" sizes="57x57" />
				<link href="/apple-touch-icon-60x60.png" rel="apple-touch-icon" sizes="60x60" />
				<link href="/apple-touch-icon-72x72.png" rel="apple-touch-icon" sizes="72x72" />
				<link href="/apple-touch-icon-76x76.png" rel="apple-touch-icon" sizes="76x76" />
				<link href="/apple-touch-icon-114x114.png" rel="apple-touch-icon" sizes="114x114" />
				<link href="/apple-touch-icon-120x120.png" rel="apple-touch-icon" sizes="120x120" />
				<link href="/apple-touch-icon-144x144.png" rel="apple-touch-icon" sizes="144x144" />
				<link href="/apple-touch-icon-152x152.png" rel="apple-touch-icon" sizes="152x152" />
				<link href="/apple-touch-icon-167x167.png" rel="apple-touch-icon" sizes="167x167" />
				<link href="/apple-touch-icon-180x180.png" rel="apple-touch-icon" sizes="180x180" />
				<link href="/apple-touch-icon-1024x1024.png" rel="apple-touch-icon" sizes="1024x1024" />
				<meta content="yes" name="apple-mobile-web-app-capable" />
				<meta content="black-translucent" name="apple-mobile-web-app-status-bar-style" />
				<meta content="Langate Кабінет" name="apple-mobile-web-app-title" />
				<link href="/favicon.ico" rel="icon" type="image/x-icon" />
				<link href="/favicon-16x16.png" rel="icon" sizes="16x16" type="image/png" />
				<link href="/favicon-32x32.png" rel="icon" sizes="32x32" type="image/png" />
				<link href="/favicon-48x48.png" rel="icon" sizes="48x48" type="image/png" />
				<title>Особистий кабінет | Langate</title>
				<Meta />
				<Links />
			</head>
			<body className="flex h-screen flex-col bg-white bg-fixed bg-repeat font-body font-medium text-main select-none xl:text-base">
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	)
}

export default App
